const REFRESH_KEY = 'tlp.rider.refreshToken';

let accessToken: string | null = null;
const listeners = new Set<() => void>();

/**
 * Where the session lives between page loads.
 *
 * The access token stays in memory only. It lasts fifteen minutes, so
 * persisting it would trade a short exposure window for an indefinite one and
 * buy nothing but skipping one refresh call on reload. The refresh token is in
 * localStorage because there is nowhere better without a same-site cookie the
 * backend does not set yet — it rotates on every use, so a stolen one is
 * single-shot and its reuse revokes the whole family server-side.
 *
 * Persisting it matters more here than in the panels: a rider opens this at
 * the start of a shift having last used it a day ago, so the cold start is the
 * normal path rather than the exception. Being asked for an OTP every morning
 * on a phone with no signal in a stairwell is how an app stops being used.
 *
 * The key is namespaced to this app. In development the four front ends sit on
 * different ports and therefore different origins anyway, but behind one host
 * in production a shared key would let a rider's browser inherit a customer
 * session, which is precisely the confusion the split exists to avoid.
 */
export const tokenStore = {
  getAccessToken: () => accessToken,

  getRefreshToken: () => {
    // Private-mode browsers and blocked storage both land here.
    try {
      return window.localStorage.getItem(REFRESH_KEY);
    } catch {
      return null;
    }
  },

  set(tokens: { accessToken: string; refreshToken: string }) {
    accessToken = tokens.accessToken;
    try {
      window.localStorage.setItem(REFRESH_KEY, tokens.refreshToken);
    } catch {
      // Storage blocked. The session still works for this tab, from memory.
    }
    emit();
  },

  clear() {
    accessToken = null;
    try {
      window.localStorage.removeItem(REFRESH_KEY);
    } catch {
      /* nothing to remove */
    }
    emit();
  },

  /** Notifies on sign-in and sign-out, including from another tab. */
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
};

function emit() {
  for (const listener of listeners) listener();
}

// A sign-out in one tab must not leave another tab holding a live session.
window.addEventListener('storage', (event) => {
  if (event.key !== REFRESH_KEY) return;
  if (event.newValue === null) {
    accessToken = null;
    emit();
  }
});
